/**
 * Copyright 2024 Design Barn Inc.
 */

'use client';

import type { ConsentCategories } from '@lottiefiles/consent-banner';
import { runConsentBanner } from '@lottiefiles/consent-banner';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { clearAllClevertapStorage } from '../libs/clevertap';
import { getStoredAcceptedCategories } from '../utils/consent-cookie';

export interface ConsentContextValue {
  /**
   * The list of accepted consent categories. Null means consent status is not yet known.
   */
  acceptedCategories: ConsentCategories[] | null;

  /**
   * Whether marketing consent has been granted.
   */
  hasMarketingConsent: boolean;

  /**
   * Whether consent bootstrap has finished for the current page load.
   */
  isConsentInitialized: boolean;

  /**
   * Whether CleverTap tracking should be gated on marketing consent.
   */
  isMarketingConsentRequired: boolean;

  /**
   * Wait until consent initialization finishes.
   */
  waitForConsentInitialization: () => Promise<void>;
}

const ConsentContext = createContext<ConsentContextValue>({
  acceptedCategories: null,
  hasMarketingConsent: false,
  isConsentInitialized: false,
  isMarketingConsentRequired: true,
  waitForConsentInitialization: async () => undefined,
});

/**
 * ConsentProvider for auth-iframe-app-new.
 *
 * Initializes `@lottiefiles/consent-banner` with disableBanners: true.
 * The iframe does NOT show the consent banner itself — the parent page handles that.
 * Instead, this reads the `lf_cookie_preferences` cookie from the shared parent domain
 * (.lottiefiles.com / .lottiefiles.dev) and provides the consent state to child components.
 *
 * When marketing consent is revoked, all CleverTap storage is torn down.
 */
export const ConsentProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [acceptedCategories, setAcceptedCategories] = useState<ConsentCategories[] | null>(getStoredAcceptedCategories);
  const [isConsentInitialized, setIsConsentInitialized] = useState<boolean>(
    () => getStoredAcceptedCategories() !== null,
  );
  const consentInitialized = useRef(false);
  const initializationWaiters = useRef<Array<() => void>>([]);

  const handleConsentChange = useCallback((categories: ConsentCategories[]) => {
    setAcceptedCategories(categories);
    setIsConsentInitialized(true);
  }, []);

  const waitForConsentInitialization = useCallback(async (): Promise<void> => {
    if (isConsentInitialized) {
      return;
    }

    await new Promise<void>((resolve) => {
      initializationWaiters.current.push(resolve);
    });
  }, [isConsentInitialized]);

  useEffect(() => {
    if (!isConsentInitialized) {
      return;
    }

    const waiters = initializationWaiters.current;

    initializationWaiters.current = [];
    waiters.forEach((resolve) => resolve());
  }, [isConsentInitialized]);

  useEffect(() => {
    if (consentInitialized.current) {
      return;
    }

    consentInitialized.current = true;

    const initializeConsent = async (): Promise<void> => {
      try {
        await runConsentBanner({
          disableBanners: true,
          callback: (consentEvent) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (consentEvent.type === 'consent_change') {
              handleConsentChange(consentEvent.acceptedCategories);
            }
          },
          trace: process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test',
        });
      } catch {
        // Ignore banner bootstrap failures and fall back to the stored cookie snapshot.
      } finally {
        setAcceptedCategories((currentCategories) => currentCategories ?? getStoredAcceptedCategories());
        setIsConsentInitialized(true);
      }
    };

    initializeConsent();
  }, [handleConsentChange]);

  // Full teardown of CleverTap when marketing consent is revoked
  useEffect(() => {
    if (acceptedCategories !== null && !acceptedCategories.includes('marketing')) {
      clearAllClevertapStorage();
    }
  }, [acceptedCategories]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      acceptedCategories,
      hasMarketingConsent: acceptedCategories !== null && acceptedCategories.includes('marketing'),
      isConsentInitialized,
      isMarketingConsentRequired: true,
      waitForConsentInitialization,
    }),
    [acceptedCategories, isConsentInitialized, waitForConsentInitialization],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = (): ConsentContextValue => useContext(ConsentContext);
